import { ReservedNodeType } from '@morph-mapper/types';
import { usePlugins } from '@morph-mapper/plugins';
import { useStore } from '@/store';
import type { StoredSuggestion, Suggestion } from '@/store';
import {
  mergeSuggestions,
  rendersSuggestionValue,
  resolveIteratorStrategy,
  suggestionCode,
  suggestionKey,
  suggestionsToRevalidate,
} from '../utils/suggestions';
import { useRenderInContext, useRenderSibling } from './use-render-in-context';

const DEFAULT_CONCURRENCY = 4;
const REBOOTSTRAP_TIMEOUT_MS = 30000;

const waitForRebootstrap = (): Promise<void> => {
  if (!useStore.getState().ai.rebootstrapInProgress) return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => {
      unsubscribe();
      clearTimeout(timer);
      resolve();
    };
    const unsubscribe = useStore.subscribe((state) => {
      if (!state.ai.rebootstrapInProgress) done();
    });
    const timer = setTimeout(done, REBOOTSTRAP_TIMEOUT_MS);
  });
};

/**
 * Runs items through `task` with at most `limit` running at the same time.
 */
export const runWithLimit = async <T>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<unknown>,
): Promise<void> => {
  const queue = [...items];
  const worker = async () => {
    for (let item = queue.shift(); item !== undefined; item = queue.shift()) {
      await task(item);
    }
  };
  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker),
  );
};

/**
 * Puts each field after the fields it depends on, otherwise keeping the
 * given order. Dependencies outside `ids` are ignored; a cycle is broken
 * where it is found.
 */
export const orderByDependencies = (
  ids: string[],
  dependenciesOf: (id: string) => string[],
): string[] => {
  const inRun = new Set(ids);
  const visited = new Set<string>();
  const ordered: string[] = [];
  const visit = (id: string) => {
    if (visited.has(id)) return;
    visited.add(id);
    dependenciesOf(id)
      .filter((dep) => inRun.has(dep))
      .forEach(visit);
    ordered.push(id);
  };
  ids.forEach(visit);
  return ordered;
};

/**
 * Evaluations running right now, per field. Every field row evaluates its own
 * field when a document loads, so this is shared by all runner instances: a
 * field that waits for a dependency finds the evaluation already running,
 * and a row whose field was already started by a dependent reuses it.
 */
const inFlight = new Map<string, Promise<StoredSuggestion[]>>();

/**
 * Evaluates fields with the plugins and keeps their suggestions in the store:
 * new suggestions, plus earlier ones that are applied or still render to
 * their value on the current document. The status per field is tracked in
 * `ai.fieldStatus`.
 */
export const useSuggestionRunner = () => {
  const { fieldSuggestions, fieldDependencies } = usePlugins();
  const renderInContext = useRenderInContext();
  const renderSibling = useRenderSibling();
  const [getById, getParentId, getType, children, getTemplateValue] = useStore(
    ({ entries: e }) => [
      e.getById,
      e.getParentId,
      e.getType,
      e.children,
      e.getTemplateValue,
    ],
  );
  const [replaceSuggestions, setFieldStatus, clearStale] = useStore(
    ({ ai }) => [ai.replaceSuggestions, ai.setFieldStatus, ai.clearStale],
  );

  const entryAccess = {
    getParentId,
    getType,
    getTemplateValue,
    getIteratorId: (mapId: string) =>
      children(mapId).getId(ReservedNodeType.Iterator),
  };

  const isIteratorEntry = (entryId: string) =>
    String(getById(entryId)?.key) === ReservedNodeType.Iterator;

  const fetchField = async (entryId: string): Promise<Suggestion[]> => {
    const entry = getById(entryId);
    // The Iterator plugin answers for the #iterator entry itself. Its
    // suggestions are not tagged `iterator: true`, so asking with that flag
    // would filter all of them out.
    const result = isIteratorEntry(entryId)
      ? await fieldSuggestions({ entry, iterator: false })
      : await fieldSuggestions({
          entry,
          iterator: false,
          iteratorStrategy: resolveIteratorStrategy(entryId, entryAccess),
          renderInContext: renderInContext(entryId),
          renderSibling: renderSibling(entryId),
        });
    return (result ?? []) as Suggestion[];
  };

  const stillValidKeys = async (
    entryId: string,
    candidates: StoredSuggestion[],
  ): Promise<Set<string>> => {
    const valid = new Set<string>();
    if (candidates.length === 0 || isIteratorEntry(entryId)) return valid;

    const render = renderInContext(entryId);
    for (const candidate of candidates) {
      const result = await render(suggestionCode(candidate));
      if (
        result.isOk() &&
        rendersSuggestionValue(result.value, candidate.value)
      ) {
        valid.add(suggestionKey(candidate));
      }
    }
    return valid;
  };

  /**
   * Waits for the fields a plugin needs evaluated before this one: the
   * evaluation running for it, or a new one. (A finished status may be from
   * the previous document, so a dependency that isn't running is evaluated
   * again.) A dependency that depends back on this field is skipped, so two
   * fields can't wait for each other.
   */
  const waitForDependencies = async (entryId: string) => {
    const waits = dependenciesOf(entryId)
      .filter((dep) => !dependenciesOf(dep).includes(entryId))
      .map((dep) => inFlight.get(dep) ?? evaluateField(dep));
    await Promise.all(waits);
  };

  /**
   * `markNewest` marks what this evaluation finds as the newest
   * suggestions; only a re-evaluation should. A field already being
   * evaluated returns that evaluation, unless this one should mark newest.
   */
  const evaluateField = (
    entryId: string,
    markNewest = false,
  ): Promise<StoredSuggestion[]> => {
    const running = inFlight.get(entryId);
    if (running && !markNewest) return running;

    const evaluation = evaluate(entryId, markNewest).finally(() => {
      if (inFlight.get(entryId) === evaluation) inFlight.delete(entryId);
    });
    inFlight.set(entryId, evaluation);
    return evaluation;
  };

  const evaluate = async (
    entryId: string,
    markNewest: boolean,
  ): Promise<StoredSuggestion[]> => {
    setFieldStatus(entryId, 'running');
    try {
      await waitForDependencies(entryId);
      await waitForRebootstrap();
      const fetched = await fetchField(entryId);
      const previous = useStore.getState().ai.suggestions[entryId] ?? [];
      const valid = await stillValidKeys(
        entryId,
        suggestionsToRevalidate(previous, fetched),
      );
      const merged = mergeSuggestions(previous, fetched, valid, markNewest);
      replaceSuggestions(entryId, merged);
      clearStale([entryId]);
      setFieldStatus(entryId, 'done');
      return merged;
    } catch (e) {
      setFieldStatus(
        entryId,
        'error',
        e instanceof Error ? e.message : String(e),
      );
      return useStore.getState().ai.suggestions[entryId] ?? [];
    }
  };

  /** The sibling fields a plugin needs evaluated before this one. */
  const dependenciesOf = (entryId: string): string[] => {
    const entry = getById(entryId);
    const parentId = getParentId(entryId);
    if (entry === undefined || parentId === undefined) return [];
    return fieldDependencies(entry).flatMap((key) => {
      try {
        const siblingId = children(parentId).getId(key);
        return siblingId === undefined ? [] : [siblingId];
      } catch {
        return [];
      }
    });
  };

  const run = async (
    entryIds: string[],
    { concurrency = DEFAULT_CONCURRENCY, markNewest = false } = {},
  ) => {
    entryIds.forEach((id) => setFieldStatus(id, 'queued'));
    // Dependencies first, so a field rarely holds a slot while it waits.
    const dependencies = new Map(entryIds.map((id) => [id, dependenciesOf(id)]));
    const ordered = orderByDependencies(entryIds, (id) => dependencies.get(id) ?? []);
    await runWithLimit(ordered, concurrency, (id) => evaluateField(id, markNewest));
  };

  return { evaluateField, run };
};
